import {useEffect, useRef, useState} from 'react'
import {DEFAULT_TIME_PICKER_OPEN_TIME, TIME_PICKER_HOURS} from './constants'
import {formatNumericTime, getHourFromNumericTime, getMinuteFromNumericTime, getMinuteValues, parseTime, toNumericTime} from './helpers'

// Internally handles time as numeric. Only accept and emit time in string formats "HH:mm" to keep consumers clean.
export const useTimePicker = ({value, defaultOpenValue, minuteStep, minTime, maxTime, disabledTimeFn, onChange, onOpenChange}) => {
	const selectedTimeAsNumeric = parseTime(value)
	const defaultTimeAsNumeric = parseTime(defaultOpenValue) ?? parseTime(DEFAULT_TIME_PICKER_OPEN_TIME)
	const minTimeAsNumeric = parseTime(minTime)
	const maxTimeAsNumeric = parseTime(maxTime)
	const minuteOptions = getMinuteValues(minuteStep)
	const displayValue = selectedTimeAsNumeric !== null ? value : ''

	const [lastSelectedTimeAsNumeric, setLastSelectedTimeAsNumeric] = useState(selectedTimeAsNumeric ?? defaultTimeAsNumeric)
	const lastSelectedHour = getHourFromNumericTime(lastSelectedTimeAsNumeric)
	const lastSelectedMinute = getMinuteFromNumericTime(lastSelectedTimeAsNumeric)
	const hasSelectedTime = useRef(selectedTimeAsNumeric !== null)

	// Update the last selected time for restoration when needed
	useEffect(() => {
		if (Number.isInteger(selectedTimeAsNumeric)) {
			hasSelectedTime.current = true
			setLastSelectedTimeAsNumeric(selectedTimeAsNumeric)
		} else if (!hasSelectedTime.current) {
			setLastSelectedTimeAsNumeric(defaultTimeAsNumeric)
		}
	}, [selectedTimeAsNumeric, defaultTimeAsNumeric])

	const isTimeDisabled = timeAsNumeric => {
		const isBeforeMinTime = minTimeAsNumeric !== null && timeAsNumeric < minTimeAsNumeric
		const isAfterMaxTime = maxTimeAsNumeric !== null && timeAsNumeric > maxTimeAsNumeric

		return (
			isBeforeMinTime ||
			isAfterMaxTime ||
			Boolean(disabledTimeFn?.(getHourFromNumericTime(timeAsNumeric), getMinuteFromNumericTime(timeAsNumeric)))
		)
	}

	const isTimeSelectable = time => {
		const timeAsNumeric = parseTime(time)

		return timeAsNumeric !== null && !isTimeDisabled(timeAsNumeric)
	}

	const getAvailableMinutes = hour => minuteOptions.filter(minute => !isTimeDisabled(toNumericTime(hour, minute)))

	// The method to actually emit the final selection to the consumer
	const commitTime = timeAsNumeric => {
		if (!Number.isInteger(timeAsNumeric) || isTimeDisabled(timeAsNumeric)) return

		hasSelectedTime.current = true
		setLastSelectedTimeAsNumeric(timeAsNumeric)
		onChange(formatNumericTime(timeAsNumeric))
	}

	const handleSelectHour = hour => {
		const availableMinutes = getAvailableMinutes(hour)
		if (availableMinutes.length === 0) return

		// Default selected minute priority:
		// 1. Last selected minute
		// 2. First available minute option
		const selectedMinute = availableMinutes.includes(lastSelectedMinute) ? lastSelectedMinute : availableMinutes[0]
		commitTime(toNumericTime(hour, selectedMinute))
	}

	const handleSelectMinute = minute => {
		commitTime(toNumericTime(lastSelectedHour, minute))
	}

	const handleSelectTime = time => commitTime(parseTime(time))

	const handleOpenChange = details => {
		onOpenChange?.(details.open)
	}

	const disabledHours = TIME_PICKER_HOURS.filter(hour => getAvailableMinutes(hour).length === 0)
	const disabledMinutes = minuteOptions.filter(minute => isTimeDisabled(toNumericTime(lastSelectedHour, minute)))

	return {
		displayValue,
		hourOptions: TIME_PICKER_HOURS,
		minuteOptions,
		disabledHours,
		disabledMinutes,
		lastSelectedHour,
		lastSelectedMinute,
		allowReselect: selectedTimeAsNumeric === null,
		timePickerContext: {
			value: value ?? '',
			validateTimeFn: isTimeSelectable,
			setValue: handleSelectTime,
			lastSelectedValue: formatNumericTime(lastSelectedTimeAsNumeric),
		},
		handleOpenChange,
		handleSelectHour,
		handleSelectMinute,
	}
}
